import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { isFetching } from "@multiweb/reactCore/fetching";
import { createGetTextCompareFn, getPrimaryText, getSecondaryText, UNKNOWN_KEY_TEXT } from "@multiweb/reactCore/i18n";
import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { AllowedMenuItemCodes, IconCode, IFormProfile, IMenuItemData, INotificationItem, MenuItemType, NotificationGroupId, NotificationItemId } from "@multiweb/types/contracts";
import { IAppState, MutableTypedObject, TypedObject } from "@multiweb/types/react";
import {
    FETCHING_KEY_MENU_ITEMS,
    FETCHING_KEY_NOTIFICATION_ITEMS,
    NO_NOTIFICATION_ITEM_ACTIVE_COLOR,
    NO_NOTIFICATION_ITEM_CREATED_DATE,
    NO_NOTIFICATION_ITEM_ICON_CODE,
    NO_NOTIFICATION_ITEM_TEXT,
    NO_NOTIFICATION_ITEM_TITLE,
    NOTIFICATIONS_PAGE_SIZE,
    ROOT_ITEM_PARENT_ID,
    UNKNOWN_FORM_ICON_CODE,
} from "./constants";
import { LOC_KEY_COLLAPSE_MENU, LOC_KEY_EXPAND_MENU } from "./locKeys";
import { logger } from "./logger";
import { ICON_CODE_COLLAPSE_MENU, ICON_CODE_EXPAND_MENU, MenuPanelItemId, UNKNOWN_ITEM_ICON_CODE } from "./panelItems";
import {
    FlatNotificationGroups,
    FormCodeMap,
    Forms,
    IFlatNotificationGroup,
    IMenuItems,
    IMenuPanelItem,
    IMenuState,
    MenuItemActivatedType,
    MenuPanelItems,
    MenuVisibility,
    NotificationItems,
    Notifications,
    ProfileFormRelationship,
    ProfileFormRelationships,
    Profiles,
    SectionId,
    VisualAutomatState,
} from "./types";
import { createFormPanelItemId, createNotificationGroupPanelItemId, isFormPanelItem, isNotificationGroupPanelItem } from "./utils";

// ----- Obecné selektory -------------------------------------------------------------------------------------------------------------------------------------

/**
 * Získá z globálního stavu podstrom patřící menu modulu.
 * @param state
 */
const getModel: Selector<IAppState, IMenuState> = (state) => state.menu;

// ----- Selektory pro menu jako celek ------------------------------------------------------------------------------------------------------------------------

/**
 * Získá pole lokalizačních klíčů pro všechny položky formulářů ve stavu.
 * Nevyužívá reselect, protože není potřeba tento selector volat často a výsledné pole může být velké. Ušetří se tak paměť.
 * @param state
 */
export const getFormItemsLocalizationKeys: Selector<IAppState, readonly string[]> = (state) => (
    Object.values(getFormItems(state))
        .map((formItem) => formItem.locKey)
);

/**
 * Získá viditelnost menu.
 */
export const getMenuVisibility: Selector<IAppState, MenuVisibility> = (state) => getModel(state).menuVisibility;

/**
 * Zjistí, zda je viditelné nepřekrývající menu.
 */
export const isMenuExpanded: Selector<IAppState, boolean> = (state) => getMenuVisibility(state) === MenuVisibility.expanded;

/**
 * Zjistí, zda je viditelné překrývající menu.
 */
export const isMenuFloated: Selector<IAppState, boolean> = (state) => getMenuVisibility(state) === MenuVisibility.floated;

/**
 * Zjistí, zda je menu viditelné - ať už překrývající nebo nepřekrývající.
 */
export const isMenuVisible: Selector<IAppState, boolean> = (state) => getMenuVisibility(state) !== MenuVisibility.hidden;

/**
 * Zjistí, zda je expandované alespoň jedno submenu.
 * @param state
 */
export const isSubmenuListExpanded: Selector<IAppState, boolean> = (state) => getModel(state).activeSubmenuId !== null;

/**
 * Zjistí, jestli zadané submenu patří mezi právě aktivní položky. Za aktivní položky se považují kromě nejzanořenější aktivní položky, která je uložena ve stavu, také
 * všechny její rodiče.
 * @param state
 * @param submenuId Id submenu, které se kontroluje.
 */
export const isSubmenuActive: Selector<IAppState, boolean, number> = (state, submenuId) => getActiveSubmenuIds(state).includes(submenuId);

/**
 * Získá id aktivního submenu, nebo null pokud není aktivní žádné.
 */
export const getActiveSubmenuId: Selector<IAppState, number | null> = (state) => getModel(state).activeSubmenuId;

/**
 * Získá id rodiče položky menu. Vrací undefined, pokud položka rodiče nemá.
 * @param state
 * @param menuItemId Id položky menu, pro kterou získáváme rodiče.
 */
export const getMenuItemParentId: Selector<IAppState, number | undefined, number> = (state, menuItemId) => {
    const menuItem = getFormItem(state, menuItemId);
    if (menuItem && menuItem.parent !== ROOT_ITEM_PARENT_ID) {
        return menuItem.parent;
    }
    return undefined;
};

/**
 * Získá funkci pro porovnávání textu podle pravidel aktivní lokalizace. Funkce je sdílená pro celé menu a neobsahuje žádné speciální nastavení.
 */
export const getTextCompareFn = createGetTextCompareFn();

/**
 * Zjistí, zda probíhá načítání položek menu.
 * @param state
 */
export const areMenuItemsLoading: Selector<IAppState, boolean> = (state) => isFetching(state, FETCHING_KEY_MENU_ITEMS);

// ----- Selektory pro menu panel -----------------------------------------------------------------------------------------------------------------------------

/**
 * Získá id aktivní sekce menu. Pokud není žádná sekce aktivní, vrací null.
 * @param state
 */
export const getActiveSectionId: Selector<IAppState, SectionId> = (state) => getModel(state).activeSectionId;

/**
 * Zjistí, zda je položka menu panelu se zadaným id aktivní.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu. Identifikátory statických položek jsou definovaný v konstantách v panelItems.ts.
 */
export const isPanelItemActive: Selector<IAppState, boolean, string> = (state, panelItemId) => getActiveSectionId(state) === panelItemId;

/**
 * Zjistí, zda je položka menu se zadaným id zvýrazněná.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu.
 */
export const isPanelItemHighlighted: Selector<IAppState, boolean, string> = (state, panelItemId) => {
    const panelItem = getPanelItem(state, panelItemId);

    if (!panelItem) {
        return false;
    }

    return !!panelItem.highlighted;
};

/**
 * Získá položky panelu menu.
 * @param state
 */
export const getPanelItems: Selector<IAppState, MenuPanelItems> = (state) => getModel(state).panelItems;

/**
 * Získá položku panelu menu. Vrací undefined, pokud není položka ve stavu.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu.
 */
export const getPanelItem: Selector<IAppState, IMenuPanelItem | undefined, string> = (state, panelItemId) => getPanelItems(state)[panelItemId];

/**
 * Získá kód ikony pro položku menu panelu podle jejího identifikátoru. Vrací výchozí hodnotu, pokud není položka ve stavu.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu. Identifikátory statických položek jsou definovaný v konstantách v panelItems.ts
 */
export const getPanelItemIconCode: Selector<IAppState, IconCode, string> = (state, panelItemId) => {
    const panelItem = getPanelItem(state, panelItemId);
    if (panelItem) {
        return panelItem.iconCode;
    }

    return UNKNOWN_ITEM_ICON_CODE;
};

/**
 * Získá nápovědu pro položku menu panelu podle jejího identifikátoru. Aby byla nápověda k dispozici, je nutné ji načíst do i18n modulu.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu. Identifikátory statických položek jsou definovány v konstantách v panelItems.ts
 * @return Vrací nápovědu pro položku menu panelu.
 * - Pro nenačtená data v i18n modulu vrací prázdný řetězec.
 * - Vrací konstantu pro neznámou lokalizaci, pokud je zadáno neznámé id nebo je načtená nápověda nedefinovaná.
 * - Pokud probíhá načítání položek menu, vrací prázdný řetězec.
 */
export const getPanelItemTitle: Selector<IAppState, string | undefined, string> = (state, panelItemId) => {
    const panelItem = getPanelItem(state, panelItemId);
    if (!panelItem) {
        if (areMenuItemsLoading(state)) {
            return "";
        }
        logger.warn("Cannot get panel item title because panel item with id %s does not exist!", panelItemId);
        return UNKNOWN_KEY_TEXT;
    }
    return getSecondaryText(state, panelItem.locKey) || "";
};

/**
 * Vrátí příznak, zda mají být zobrazeny popisky položek v menu panelu.
 * @param state
 */
export const areMenuPanelCaptionsVisible: Selector<IAppState, boolean> = (state) => getModel(state).menuPanelCaptionsVisible;

/**
 * Zjistí, zda se má zobrazit popisek pro danou položku menu panelu. Vrací false, pokud není nastaveno zobrazování popisků v menu panelu,
 * nebo pokud není povoleno zobrazení popisku u dané položky.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu.
 */
export const isPanelItemCaptionEnabled: Selector<IAppState, boolean, string> = (state, panelItemId) => {
    if (!areMenuPanelCaptionsVisible(state)) {
        return false;
    }
    return panelItemId !== MenuPanelItemId.user;
};

/**
 * Získá popisek pro položku menu panelu podle jejího identifikátoru. Aby byl popisek k dispozici, je nutné ho načíst do i18n modulu.
 * @param state
 * @param panelItemId Identifikátor položky panelu menu. Identifikátory statických položek jsou definovány v konstantách v panelItems.ts
 * @return Vrací popisek pro položku menu panelu.
 * - Vrací undefined v případe, že není zobrazování popisku povoleno.
 * - Pokud probíhá načítání položek menu nebo nejsou načtená data v i18n modulu, vrací prázdný řetězec.
 * - Vrací konstantu pro neznámou lokalizaci, pokud je zadáno neznámé id nebo je načtený popisek nedefinovaný.
 */
export const getPanelItemCaption: Selector<IAppState, string | undefined, string> = (state, panelItemId) => {
    if (!isPanelItemCaptionEnabled(state, panelItemId)) {
        return undefined;
    }
    const panelItem = getPanelItem(state, panelItemId);
    if (!panelItem) {
        if (areMenuItemsLoading(state)) {
            return "";
        }
        logger.warn("Cannot get panel item caption because panel item with id %s does not exist!", panelItemId);
        return UNKNOWN_KEY_TEXT;
    }
    return getPrimaryText(state, panelItem.locKey) || "";
};

/**
 * Vrací id položky reprezentované v položce menu panelu. Pokud se jedná o statickou položku, vrací id menu panel položky jako takové.
 * @param state
 * @param panelItemId Id položky menu panelu, pro který se získává id reprezentované položky.
 */
export const getPanelItemItemId: Selector<IAppState, number | string, string> = (state, panelItemId) => (
    getPanelItem(state, panelItemId)?.itemId ?? panelItemId
);

/**
 * Získá aktivační typ položky menu panelu. Slouží pro custom akce a má větší granularitu než typ položky menu panelu.
 * @param state
 * @param panelItemId Id položky menu panelu, pro který se získává aktivační typ.
 */
export const getPanelItemActivationType: Selector<IAppState, MenuItemActivatedType | undefined, string> = (state, panelItemId) => (
    getPanelItem(state, panelItemId)?.activationType
);

/**
 * Získá Id naposledy otevřeného podmenu položky menu panelu.
 * @param state
 * @param panelItemId Id položky menu panelu, pro který se získává Id podmenu.
 */
export const getPanelItemLastSubmenuId: Selector<IAppState, number | undefined, string> = (state, panelItemId) => (
    getPanelItem(state, panelItemId)?.lastSubmenuId
);

/**
 * Zjistí, zda položka menu panelu je typu kombinované položky, která slouží zároveň jako formulář i složka.
 * @param state
 * @param panelItemId Id položky menu panelu, pro který se zjišťuje, zda je kombinovaný.
 */
export const isPanelItemCombinedType: Selector<IAppState, boolean, string> = (state, panelItemId) => getPanelItemActivationType(state, panelItemId) === MenuItemActivatedType.panelItemCombinedItemForm;

/**
 * Získá kód ikony pro MenuExpandButton v závislosti na tom, jestli je menu viditelné nebo skryté.
 * @param state
 */
export const getExpandButtonIconCode: Selector<IAppState, IconCode> = (state) => isMenuExpanded(state) ? ICON_CODE_COLLAPSE_MENU : ICON_CODE_EXPAND_MENU;

/**
 * Získá popisek pro MenuExpandButton v závislosti na tom, jestli je menu viditelné nebo skryté. Aby byl popisek k dispozici,
 * je nutné ho načíst do i18n modulu.
 * @param state
 */
export const getExpandButtonTitle: Selector<IAppState, string | undefined> = (state) => (
    getSecondaryText(state, isMenuExpanded(state) ? LOC_KEY_COLLAPSE_MENU : LOC_KEY_EXPAND_MENU)
);

// ----- Selektory pro formulářové položky --------------------------------------------------------------------------------------------------------------------

/**
 * Získá z globálního stavu podstrom patřící položkám menu modulu.
 * @param state
 */
const getItemsModel: Selector<IAppState, IMenuItems> = (state) => getModel(state).items;

/**
 * Vrací všechny formulářové položky.
 * @param state
 */
export const getFormItems: Selector<IAppState, Forms> = (state) => getItemsModel(state).forms;

/**
 * Vrací všechny položky profilů.
 * @param state
 */
export const getProfiles: Selector<IAppState, Profiles> = (state) => getItemsModel(state).profiles;

/**
 * Získá menu položku formuláře podle zadaného id. Pokud formulář pro zadané id neexistuje, vrací undefined.
 * @param state
 * @param formItemId Id formuláře, který chceme získat.
 */
export const getFormItem: Selector<IAppState, IMenuItemData | undefined, number> = (state, formItemId) => getFormItems(state)[formItemId];

/**
 * Získá profil formuláře podle zadaného id.
 * @param state
 * @param profileId Id profilu formuláře, který chceme získat. Pokud profil pro zadané id neexistuje, vrací undefined.
 */
export const getFormProfile: Selector<IAppState, IFormProfile | undefined, number> = (state, profileId) => getProfiles(state)[profileId];

/**
 * Získá pole povolených aliasů kořenových uzlů hlavního navigačního menu. Vrací null, pokud nejsou alisy omezeny.
 * @state
 */
export const getAllowedMenuItemCodes: Selector<IAppState, AllowedMenuItemCodes> = (state) => getItemsModel(state).allowedMenuItemCodes;

/**
 * Získá identifikátory kořenových forlumářových položek.
 * @param state
 */
export const getRootFormItemIds: Selector<IAppState, readonly number[]> = (state) => getItemsModel(state).rootFormItemIds;

/**
 * Získá objekt s kořenovými formulářovými položkami. Obejkt je klíčovaný přes identifikátory formulářů.
 * @param state
 */
export const getRootFormItems = createSelector<IAppState, ReturnType<typeof getFormItems>, ReturnType<typeof getRootFormItemIds>, Forms>(
    getFormItems,
    getRootFormItemIds,
    (formItems, rootFormItemIds) => (
        rootFormItemIds.reduce((acc, id) => ({
            ...acc,
            [id]: formItems[id],
        }), {})
    ),
);

/**
 * Získá pole identifikátorů kořenových formulářových položek pro menu formulářů.
 * Položky jsou případně filtrovány na základě povolených aliasů v souboru initParams.json.
 * @param state
 */
export const getAllowedRootFormItemIds = createSelector<IAppState, ReturnType<typeof getRootFormItems>, ReturnType<typeof getAllowedMenuItemCodes>, ReturnType<typeof getRootFormItemIds>, readonly number[]>(
    getRootFormItems,
    getAllowedMenuItemCodes,
    getRootFormItemIds,
    (rootFormItems, allowedMenuItemCodes, rootFormItemIds) => {
        if (!allowedMenuItemCodes) {
            return rootFormItemIds;
        }

        return Object.values(rootFormItems)
            .filter(({ code }) => code ? allowedMenuItemCodes.includes(code) : false)
            .map(({ id }) => id);
    },
);

/**
 * Získá identifikátory formulářových položek panelu menu
 */
export const getFormPanelItemIds = createSelector<IAppState, ReturnType<typeof getAllowedRootFormItemIds>, SectionId[]>(
    getAllowedRootFormItemIds,
    (allowedRootFormItemIds) => allowedRootFormItemIds.map((id) => createFormPanelItemId(id)),
);

/**
 * Získá identifikátor formulářové položky právě aktivní sekce.
 * @param state
 * @return Identifikátor formulářové položky - vrací undefined v případě, že vybraná sekce nereprezentuje formulářovou položku.
 */
export const getActiveSectionFormItemId: Selector<IAppState, number | undefined> = (state) => {
    const activeSectionId = getActiveSectionId(state);

    const panelItem = getPanelItem(state, activeSectionId);
    if (!panelItem || !isFormPanelItem(panelItem) || !panelItem.itemId) {
        return undefined;
    }

    return panelItem.itemId as number;
};

/**
 * Získá id potomků zadaného formuláře. Pokud formulář neexistuje, nebo nemá potomky, vrací prázdné pole.
 * @param state
 * @param formId Id formuláře, jehož potomky chceme získat.
 */
export const getFormChildIds: Selector<IAppState, readonly number[], number> = (state, formId) => {
    const form = getFormItem(state, formId);
    return form && form.children ? form.children : EMPTY_ARRAY;
};

/**
 * Vytvoří cachovaný selektor pro získání identifikátorů všech profilů patřících k formuláři se zadaným id.
 */
export const createGetProfileIdsForForm = () => createSelector(
    getFormItemCode,
    getProfiles,
    (formCode, profiles) => Object.values(profiles)
        .filter((profile) => profile.formCode === formCode && profile.showInNavigationTree === true)
        .sort((first, second) => (first.title.localeCompare(second.title)))
        .map((profile) => profile.id),
);

/**
 * Získá kód ikony pro formulář se zadaným id. Pokud formulář neexistuje, nebo nemá ikonu, vrací náhradní ikonu
 * definovanou konstantou UNKNOWN_FORM_ICON_CODE.
 * @param state
 * @param formId Id formuláře.
 */
export const getFormItemIconCode: Selector<IAppState, IconCode | undefined, number> = (state, formId) => {
    const form = getFormItem(state, formId);
    return form ? form.icon : UNKNOWN_FORM_ICON_CODE;
};

/**
 * Získá přeložený název formuláře se zadaným id.
 * @param state
 * @param formItemId Id menu položky formuláře.
 * @return Vrací název formuláře.
 * - Vrací hodnotu z konstanty UNKNOWN_KEY_TEXT, pokud formulář neexistuje nebo nemá validní lokalizační klíč.
 * - Pokud probíhá načítání položek menu, vrací prázdný řetězec.
 */
export const getFormItemTitle: Selector<IAppState, string | undefined, number> = (state, formItemId) => {
    const form = getFormItem(state, formItemId);
    if (!form) {
        if (areMenuItemsLoading(state)) {
            return "";
        }
        logger.warn("Cannot get form item title because form item with id %s does not exist!", formItemId);
        return UNKNOWN_KEY_TEXT;
    }
    return getPrimaryText(state, form.locKey);
};

/**
 * Pokusí se získat formulářovou položku pro profil navázanou přes kód formuláře. Vazba mezi formulářem a položkami formuláře je 1:N. Můžou tedy nastat případy,
 * kdy je vyhledáno více položek. V takovém případě se vybere první nalezená. Vrací undefined, pokud neexistuje žádná položka.
 * @param state
 * @param profileId Id profilu, pro který se získá formulář.
 */
export const getFormItemByProfileId: Selector<IAppState, IMenuItemData | undefined, number> = (state, profileId) => {
    const profile = getFormProfile(state, profileId);
    if (!profile) {
        return undefined;
    }
    const formItemId = getFormItemId(state, profile.formCode);
    if (!formItemId) {
        return undefined;
    } else {
        return getFormItem(state, formItemId);
    }
};

/**
 * Získá identifikátor formuláře pro zadaný kód formuláře.
 * - Vrací undefined v případě, že nebyl identifikátor nalezen.
 * - Vrací některý z nalezených identifikátorů, pokud jich existuje více.
 * @param state
 * @param formCode Kód formuláře
 */
export const getFormItemId: Selector<IAppState, number | undefined, string> = (state, formCode) => {
    const formItemIds = getFormItemIds(state, formCode);

    if (!formItemIds || formItemIds.length === 0) {
        return undefined;
    }

    if (formItemIds.length !== 1) {
        logger.warn("Cannot get unique form item identifier for a form code %s, because more form items are related " +
            "to the same form code. Random identifier has been used.", formCode);
    }

    return formItemIds[0];
};

/**
 * Získá mapu pro převod kódu formuláře na jeho id.
 * @param state
 */
export const getFormCodeMap: Selector<IAppState, FormCodeMap> = (state) => getItemsModel(state).formCodeMap;

/**
 * Získá id všech formulářových položek navázaných na formulář se zadaným kódem. Pro neznámý kód formuláře vrací undefined.
 * @param state
 * @param formCode Kód formuláře.
 */
export const getFormItemIds: Selector<IAppState, readonly number[] | undefined, string> = (state, formCode) => getFormCodeMap(state)[formCode];

/**
 * Získá jméno profilu podle jeho id. Pokud profil neexistuje, vrací hodnotu konstanty UNKNOWN_KEY_TEXT.
 * @param state
 * @param profileId Id profilu, pro který se získává jméno.
 */
export const getProfileTitle: Selector<IAppState, string, number> = (state, profileId) => {
    const profile = getFormProfile(state, profileId);
    return profile ? profile.title : UNKNOWN_KEY_TEXT;
};

/**
 * Získá kód formuláře z profilu se zadaným id.
 * @param state
 * @param profileId
 */
export const getProfileFormCode: Selector<IAppState, string | undefined, number> = (state, profileId) => getFormProfile(state, profileId)?.formCode;

/**
 * Získá kód formuláře podle jeho id. Pokud formulář se zadaným id neexistuje, vrací undefined.
 * @param state
 * @param formId
 */
export const getFormItemCode: Selector<IAppState, string | undefined, number> = (state, formId) => getFormItem(state, formId)?.formCode;

/**
 * Získá podstrom stavu reprezentující propojení mezi profily a formulářovými položkami.
 * @param state
 */
export const getProfileFormRelationshipModel: Selector<IAppState, ProfileFormRelationships> = (state) => getItemsModel(state).profileFormRelationships;

/**
 * Získá objekt reprezentující spojení mezi profilem a položkou formuláře podle složeného id generovaného z {@link createItemIdForProfile}.
 * @param state
 * @param itemId
 */
export const getProfileFormRelationship: Selector<IAppState, ProfileFormRelationship | undefined, string> = (state, itemId) => getProfileFormRelationshipModel(state)[itemId];

/**
 * Vytvoří cachovaný selector pro získání mapování id formuláře na jeho lokalizované jméno.
 * Selector se přepočítá, pokud se změní lokalizace pro některý ze zadaných formulářů.
 * @param formIds Id formulářů, pro které se vytvoří objekt s lokalizovanými názvy.
 */
export const createGetFormNameMap = (formIds: readonly number[]) => createSelector(
    formIds.map((formId) => (state: IAppState) => getFormItemTitle(state, formId)),
    (...formNames) => formNames.reduce((accumulator, formName, index) => {
        const formId = formIds[index];
        if (formId !== undefined && formName !== undefined) {
            accumulator[formId] = formName;
        }
        return accumulator;
    }, {} as MutableTypedObject<string>) as TypedObject<string>,
);

/**
 * Zjistí, zda je položka typu kombinované položky.
 * @param state
 * @param formId Id zkoumané položky menu.
 */
export const isCombinedItem: Selector<IAppState, boolean, number> = (state, formId) => getFormItem(state, formId)?.type === MenuItemType.combined;
// ----- Selektory pro notifikace ----------------------------------------------------------------------------------------------------------

/**
 * Získá z globálního stavu podstrom patřící notifikacím menu modulu.
 * @param state
 */
const getNotificationsModel: Selector<IAppState, Notifications> = (state) => getItemsModel(state).notifications;

/**
 * Získá notifikační skupinu. Vrací undefined, pokud není skupina ve stavu.
 * @param state
 * @param notificationGroupId Identifikátor notifikační skupiny
 */
export const getNotificationGroup: Selector<IAppState, IFlatNotificationGroup | undefined, NotificationGroupId> = (state, notificationGroupId) => (
    getNotificationGroups(state)[notificationGroupId]
);

/**
 * Získá identifikátory notifikačních položek. Identifikátory jsou seřazeny podle okamžiku vzniku jejich notifikačních položek
 * od nejnovějších po nejstarší. Změna doby běhu funkce při použití reselectu je zanedbatelná.
 * @param state
 */
export const getNotificationItemIds: Selector<IAppState, NotificationItemId[]> = (state) => (
    Object.values(getNotificationItems(state))
        .sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime())
        .map(({ id }) => id)
);

/**
 * Získá počet načtených stránek notifikačních položek.
 * @param state
 */
export const getNotificationsPageCount: Selector<IAppState, number> = (state) => getNotificationsModel(state).pageCount;

/**
 * Získá počáteční index pro načítání další stránky notifikačních položek.
 * @param state
 */
export const getNotificationsStartIndex: Selector<IAppState, number> = (state) => getNotificationsPageCount(state) * NOTIFICATIONS_PAGE_SIZE;

/**
 * Zjistí, zda jsou již načteny všechny notifikační položky. Použití reselectu by nezvýšilo výkon.
 * @param state
 */
export const allNotificationItemsLoaded: Selector<IAppState, boolean> = (state) => {
    const notificationItemsCount = getNotificationItemIds(state).length;
    const pageCount = getNotificationsPageCount(state);
    const isNextPageLoading = isFetching(state, FETCHING_KEY_NOTIFICATION_ITEMS);

    return !isNextPageLoading && (notificationItemsCount < pageCount * NOTIFICATIONS_PAGE_SIZE);
};

/**
 * Získá notifikační položky.
 * @param state
 */
export const getNotificationItems: Selector<IAppState, Readonly<NotificationItems>> = (state) => getNotificationsModel(state).items;

/**
 * Zjistí, zda jsou ve stavu dostupné nějaké notifikační položky.
 * @param state
 */
export const areNotificationItemsAvailable: Selector<IAppState, Readonly<boolean>> = (state) => getNotificationItemIds(state).length !== 0;

/**
 * Získá notifikační skupiny.
 * @param state
 */
export const getNotificationGroups: Selector<IAppState, Readonly<FlatNotificationGroups>> = (state) => getNotificationsModel(state).groups;

/**
 * Získá překladové klíče notifikačních skupin. Použití reselectu by nezvýšilo výkon.
 * @param state
 */
export const getNotificationGroupsLocalizationKeys: Selector<IAppState, ReadonlyArray<NotificationGroupId>> = (state) => {
    const notificationGroups = getNotificationGroups(state);
    return Object.values(notificationGroups).map((notificationGroup) => notificationGroup.titleLocKey);
};

/**
 * Cacheovaný selektor, který získá identifikátory notifikačních skupin.
 * @param state
 */
export const getNotificationGroupIds = createSelector<IAppState, ReturnType<typeof getNotificationGroups>, NotificationGroupId[]>(
    state => getNotificationGroups(state),
    (notificationGroups) => Object.keys(notificationGroups),
);

/**
 * Cacheovaný selektor, který získá identifikátory položek panelu menu reprezentujících notifikační skupiny.
 * @param state
 */
export const getNotificationGroupPanelItemIds = createSelector<IAppState, ReturnType<typeof getNotificationGroupIds>, NotificationGroupId[]>(
    state => getNotificationGroupIds(state),
    (notificationGroupIds) => notificationGroupIds.map((id) => createNotificationGroupPanelItemId(id)),
);

/**
 * Zjistí, zda-li je zadaná hodnota identifikátorem notifikační skupiny.
 * @param state
 * @param value Hodnota pro kontrolu
 */
export const isNotificationGroupId: Selector<IAppState, boolean, string> = (state, value) => (
    getNotificationGroupIds(state).includes(value)
);

/**
 * Zjistí, zda je zadaná sekce prázdná. Tedy že se pro ni nemá otevírat menu.
 * @param menuPanelItemId Identifikátor položky panelu menu
 * @param state
 */
export const isEmptySection: Selector<IAppState, boolean, string> = (state, menuPanelItemId) => {
    const panelItem = getPanelItem(state, menuPanelItemId);
    if (!panelItem) {
        return true;
    }
    if (isFormPanelItem(panelItem)) {
        const formItem = getFormItem(state, panelItem.itemId as number);
        if (!formItem || formItem.type === MenuItemType.form) {
            return true;
        }
        if (formItem.type === MenuItemType.folder || formItem.type === MenuItemType.combined) {
            if (!formItem.children || formItem.children.length === 0) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Zjistí, zda se má po vybrání položky panelu menu provést změna aktivní sekce.
 * @param state
 * @param menuPanelItemId Identifikátor položky panelu menu
 */
export const shouldChangeActiveSection: Selector<IAppState, boolean, string> = (state, menuPanelItemId) => {
    const activeSectionId = getActiveSectionId(state);
    if (activeSectionId !== menuPanelItemId) {
        return !isEmptySection(state, menuPanelItemId);
    }
    return false;
};

/**
 * Zjistí, zda je zadaná hodnota identifikátorem položky panelu menu reprezentující notifikační skupinu.
 * @param state
 * @param menuPanelItemId Identifikátor položky panelu menu
 */
export const isNotificationGroupPanelItemId: Selector<IAppState, boolean, string> = (state, menuPanelItemId) => {
    const panelItem = getPanelItem(state, menuPanelItemId);
    if (!panelItem) {
        return false;
    }
    return isNotificationGroupPanelItem(panelItem);
};

/**
 * Získá nadpis aktivní notifikační sekce. V případě, že není aktivní notifikační sekce nebo sekce neexistuje se vrací předem definovaný řetězec.
 * @param state
 */
export const getActiveNotificationGroupTitle: Selector<IAppState, string | undefined> = (state) => {
    const activeSectionId = getActiveSectionId(state);

    const panelItem = getPanelItem(state, activeSectionId);
    if (!panelItem || !isNotificationGroupPanelItem(panelItem)) {
        return UNKNOWN_KEY_TEXT;
    }

    return getPrimaryText(state, panelItem.locKey);
};

/**
 * Zjistí, zda je notifikační skupina zvýrazněná / zda skupina obsahuje nepřečtenou notifikaci. Vrací false, pokud zadaná notifikační skupina neexistuje.
 * @param notificationGroupId Identifikátor notifikační skupiny
 * @param state
 */
export const isNotificationGroupHighlighted: Selector<IAppState, boolean, NotificationGroupId> = (state, notificationGroupId) => {
    const notificationGroup = getNotificationGroup(state, notificationGroupId);
    if (!notificationGroup) {
        return false;
    }

    return notificationGroup.unread;
};

/**
 * Získá notifikační položku. Vrací undefined, pokud hledaná notifikační položka neexistuje.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItem: Selector<IAppState, INotificationItem | undefined, NotificationItemId> = (state, notificationItemId) => (
    getNotificationItems(state)[notificationItemId]
);

/**
 * Získá barvu aktivní notifikační položky. Vrací předem definovanou výchozí barvu, pokud notifikační položka nebyla nalezena.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItemActiveColorHex: Selector<IAppState, string, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);

    if (!notificationItem) {
        return NO_NOTIFICATION_ITEM_ACTIVE_COLOR;
    }

    return notificationItem.type.color;
};

/**
 * Získá kód ikony notifikační položky. Vrací předem definovaný kód ikony, pokud notifikační položka nebyla nalezena.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItemIconCode: Selector<IAppState, IconCode, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);

    if (!notificationItem) {
        return NO_NOTIFICATION_ITEM_ICON_CODE;
    }

    return notificationItem.type.iconCode;
};

/**
 * Získá obsah zprávy notifikační položky. Vrací předem definovaný řetězec, pokud nebyla nalezena notifikační položka.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItemText: Selector<IAppState, string | undefined, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);

    if (!notificationItem) {
        return NO_NOTIFICATION_ITEM_TEXT;
    }

    return notificationItem.text;
};

/**
 * Získá nadpis notifikační položky. Vrací předem definovaný řetězec, pokud nebyla nalezena notifikační položka.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItemTitle: Selector<IAppState, string | undefined, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);

    if (!notificationItem) {
        return NO_NOTIFICATION_ITEM_TITLE;
    }

    return notificationItem.title;
};

/**
 * Získá datum vytvoření notifikační položky. Vrací předem definovanou hodnotu, pokud nebyla nalezena notifikační položka.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const getNotificationItemCreatedDate: Selector<IAppState, Date, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);
    if (!notificationItem) {
        logger.warn("Selected notification item with id %d does not exist! Default date considered as created for a notification" +
            " item component.", notificationItemId);
        return NO_NOTIFICATION_ITEM_CREATED_DATE;
    }

    return notificationItem.createdDate;
};

/**
 * Zjistí, zda je notifikace nepřečtená. Vrací false, pokud nebyla položka nalezena.
 * @param state
 * @param notificationItemId Identifikátor notifikační položky.
 */
export const isNotificationItemUnread: Selector<IAppState, boolean, NotificationItemId> = (state, notificationItemId) => {
    const notificationItem = getNotificationItem(state, notificationItemId);
    if (!notificationItem) {
        return false;
    }

    return notificationItem.unread;
};

/**
 * Zjistí, zda je notifikační skupina prázdná.
 * @param state
 */
export const isNotificationGroupEmpty: Selector<IAppState, boolean> = (state) => {
    const notificationItemsAvailable = areNotificationItemsAvailable(state);
    const hasNextPage = !allNotificationItemsLoaded(state);

    return !hasNextPage && !notificationItemsAvailable;
};

/**
 * Zjistí, zda jsou pro formulářovou položku se zadaným id povoleny akce pro otevření nové instance.
 * @param state
 * @param formItemId Id formulářové položky menu.
 */
export const areFormItemWindowActionsEnabled: Selector<IAppState, boolean, number> = (state, formItemId) => !getItemsModel(state).restrictions.formItemHideWindowActions[formItemId];

/**
 * Zjistí, zda jsou pro profilovou položku se zadaným id povoleny akce pro otevření nové instance.
 * @param state
 * @param profileId Id profilové položky menu.
 */
export const areProfileWindowActionsEnabled: Selector<IAppState, boolean, number> = (state, profileId) => !getItemsModel(state).restrictions.profileHideWindowActions[profileId];

/**
 * Zjistí, zda je pro danou instanci povolena akce zavření.
 * @param state
 * @param formInstanceId Id otevřené instance formuláře.
 */
export const isFormItemInstanceCloseActionEnabled: Selector<IAppState, boolean, string> = (state, formInstanceId) => !getItemsModel(state).restrictions.formItemInstanceHideCloseAction[formInstanceId];

/**
 * Zjistí, zda je zadaná položka menu aktivní.
 * @param state
 * @param formItemId Id formulářové položky menu.
 */
export const isMenuItemActive: Selector<IAppState, boolean, number> = (state, formItemId) => getActiveSubmenuIds(state).includes(formItemId);

/**
 * Získá pole id submenu prvků od aktivního prvku, přes jeho rodiče, až po potomka kořenového prvku. Pole je seřazené tak, že začíná
 * potomkem kořenového prvku a končí aktivním prvkem. Pokud není žádný prvek aktivní, vrací prázdné pole. Jsou použity pouze id
 * existujících položek.
 */
export const getActiveSubmenuIds = createSelector<
    IAppState,
    ReturnType<typeof getActiveSubmenuId>,
    ReturnType<typeof getAllowedRootFormItemIds>,
    ReturnType<typeof getFormItems>,
    readonly number[]
>(
    getActiveSubmenuId,
    getAllowedRootFormItemIds,
    getFormItems,
    (activeSubmenuId, allowedRootFormItemIds, formItems) => {
        let currentSubmenuId = activeSubmenuId;
        if (currentSubmenuId) {
            const activeSubmenuIds = [];
            do {
                if (!currentSubmenuId) {
                    break;
                }
                const submenuItem: IMenuItemData | undefined = formItems[currentSubmenuId];
                if (!submenuItem) {
                    break;
                }
                activeSubmenuIds.push(currentSubmenuId);
                currentSubmenuId = submenuItem.parent;
            } while (!allowedRootFormItemIds.includes(currentSubmenuId));
            return activeSubmenuIds.reverse();
        } else {
            return EMPTY_ARRAY;
        }
    },
);

/**
 * Zjistí, zda je viditelná překryvná vrsta formulářové oblasti.
 */
export const isFormOverlayVisible: Selector<IAppState, boolean> = (state) => getModel(state).formOverlayVisible;

/**
 * Získá aktuální stav vizuálního automatu reprezentovaný řetězcovou hodnotou.
 */
export const getVisualAutomatState: Selector<IAppState, VisualAutomatState | undefined> = (state) => {
    const formOverlayVisible = isFormOverlayVisible(state);
    const menuVisibility = getMenuVisibility(state);
    const submenuListExpanded = isSubmenuListExpanded(state);

    if (!formOverlayVisible && menuVisibility === MenuVisibility.hidden && !submenuListExpanded) {
        return "000";
    }
    if (!formOverlayVisible && menuVisibility === MenuVisibility.expanded && !submenuListExpanded) {
        return "020";
    }
    if (formOverlayVisible && menuVisibility === MenuVisibility.expanded && submenuListExpanded) {
        return "121";
    }
    if (formOverlayVisible && menuVisibility === MenuVisibility.floated && !submenuListExpanded) {
        return "110";
    }
    if (formOverlayVisible && menuVisibility === MenuVisibility.floated && submenuListExpanded) {
        return "111";
    }

    return undefined;
};

/**
 * Získá příznak, zda je aktviní float přechod menu
 */
export const isMenuFloatTransitionActive: Selector<IAppState, boolean> = (state) => getModel(state).menuFloatTransitionActive;
